import React, { useContext, useEffect, useState } from "react";
import { MessageDescriptor, defineMessages, useIntl } from "react-intl";
import { sessionStore } from "../../contexts/SessionContext";

import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { Button } from "@evidenceb/athena-common/design-system/Button";
import Input from "../../design-system-components/Input/Input";
import Card from "../../design-system-components/Card/Card";
import Grid from "../../components/Grid/Grid";
import GridColumn from "../../components/Grid/GridColumn";
import UserCard from "../../components/UserCard/UserCard";
import { Link } from "react-router-dom";
import classNames from "classnames";
import {
    checkSpecificHierarchy,
    getExtremity,
    TreeNode,
    useNodeCheckingStrategy,
} from "../TreeCheckboxGroup";
import StudentPicker from "../../pages/DashBoard/DashboardTeacher/Group/Components/StudentPicker/StudentPicker";
import { getStudent } from "../../utils/dataRetrieval";
import { Group } from "../../interfaces/Session";
import useOnlineStatus from "../../hooks/useOnlineStatus";
import OfflineBanner from "../../pages/OfflineBanner/OfflineBanner";

import "./GroupForm.scss";

const TITLE_MAX_LENGTH = 200;

export type GroupFormValues = {
    name: string;
    students: string[];
    title?: string;
};

export type GroupFormProps = {
    defaultValues: GroupFormValues;
    onSubmit: (values: GroupFormValues) => void;
    students: Record<string, TreeNode>;
    messages: {
        description: MessageDescriptor;
        submit: MessageDescriptor;
        backTo: MessageDescriptor;
        groupName: MessageDescriptor;
        groupNameInstruction: MessageDescriptor;
    };
    goBackUrl: string;
};

const GroupForm = ({
    defaultValues,
    onSubmit,
    students,
    messages,
    goBackUrl,
}: GroupFormProps) => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const {
        session: { classrooms },
    } = useContext(sessionStore);

    const [groupName, setGroupName] = useState(defaultValues?.name);

    const {
        checkedLeaves: checkedStudents,
        toggleNode: toggleStudents,
        setCheckedLeaves: setCheckedStudents,
    } = useNodeCheckingStrategy(students ?? {}, checkSpecificHierarchy);

    useEffect(() => {
        if (defaultValues?.students) {
            const checkedStudents = buildHierarchyPaths(
                defaultValues.students,
                classrooms
            );
            setCheckedStudents(checkedStudents, false);
        }
    }, [classrooms, defaultValues, setCheckedStudents]);

    // const onStudentSearch = (value: string) => {
    //     const newStudents = checkSpecificHierarchy(
    //         value,
    //         checkedStudents,
    //         students
    //     );
    //     setCheckedStudents(newStudents, true);
    // };

    return (
        <>
            <div className="group-form">
                <OfflineBanner />

                <Grid noGutters className="group-form__grid">
                    <GridColumn col={4} className="group-form__aside">
                        <aside>
                            <h2>
                                {intl.formatMessage(
                                    defaultMessages.classroomList
                                )}
                            </h2>

                            <p>{intl.formatMessage(messages.description)}</p>
                            <Card
                                className="group-form__aside__card"
                                contentClassName="group-form__aside__card-content"
                                colors={{ background: "var(--block)" }}
                            >
                                <StudentPicker
                                    students={students}
                                    classroomIds={
                                        classrooms?.map(({ id }) => id) ?? []
                                    }
                                    pickedStudents={checkedStudents}
                                    onStudentPick={toggleStudents}
                                    startingHeadingLevel={3}
                                />
                            </Card>
                        </aside>
                    </GridColumn>
                    <GridColumn col={8} className="group-form__main">
                        <main>
                            <div className="group-form__main__title-container">
                                <Link to={goBackUrl}>
                                    <Icon
                                        path="chevron_left"
                                        color="var(--text-main-default)"
                                        size="large"
                                    />
                                    <VisuallyHidden>
                                        {intl.formatMessage(messages.backTo)}
                                    </VisuallyHidden>
                                </Link>
                                <h1>
                                    {defaultValues?.title ?? defaultValues.name}
                                </h1>
                            </div>
                            <form className="group-form__main__name-input">
                                <label htmlFor="group-input-name">
                                    {intl.formatMessage(messages.groupName)}
                                </label>
                                <Input
                                    id="group-input-name"
                                    type="text"
                                    placeholder={intl.formatMessage(
                                        messages.groupNameInstruction
                                    )}
                                    value={
                                        groupName === defaultValues?.name
                                            ? ""
                                            : groupName
                                    }
                                    onChange={(e) =>
                                        setGroupName(e.currentTarget.value)
                                    }
                                    maxLength={TITLE_MAX_LENGTH}
                                    colors={{
                                        borderHover:
                                            "var(--element-teacher-default)",
                                    }}
                                />
                            </form>
                            <Card
                                colors={{ background: "var(--block)" }}
                                className={classNames(
                                    "group-form__main__added-students-list",
                                    {
                                        "--has-students":
                                            checkedStudents.length > 0,
                                    }
                                )}
                            >
                                {checkedStudents.length ? (
                                    <ul>
                                        {checkedStudents.map((path) => {
                                            const studentNode = getExtremity(
                                                path,
                                                students
                                            );
                                            const student = getStudent(
                                                studentNode.id,
                                                classrooms!
                                            );

                                            return (
                                                <li key={student.id}>
                                                    <UserCard
                                                        user={student}
                                                        className="group-form__main__user-card"
                                                        contentClassName="group-form__main__user__content"
                                                        nameClassName="group-form__main__user-name"
                                                    />
                                                    <button
                                                        className="group-form__main__user__toggle"
                                                        onClick={() =>
                                                            toggleStudents(path)
                                                        }
                                                    >
                                                        <Icon
                                                            path="close"
                                                            className="group-form__main__user__toggle__icon"
                                                        />
                                                        <VisuallyHidden>
                                                            {intl.formatMessage(
                                                                defaultMessages.groupCreationRemoveStudent
                                                            )}
                                                        </VisuallyHidden>
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                ) : (
                                    <div className="group-form__main__added-students-list--empty">
                                        <p>
                                            {intl.formatMessage(
                                                defaultMessages.noAddedStudents
                                            )}
                                        </p>
                                    </div>
                                )}
                            </Card>
                        </main>
                    </GridColumn>
                </Grid>
                <Grid noGutters className="group-form__footer--container">
                    <GridColumn col={12} className="group-form__footer">
                        <Button
                            label={intl.formatMessage(defaultMessages.cancel)}
                            variant="primary"
                            asRouterLink={{ to: goBackUrl }}
                        />
                        <Button
                            label={intl.formatMessage(messages.submit)}
                            variant="custom"
                            colors={{
                                background: "var(--element-teacher-default)",
                                text: "var(--text-main-inverted)",
                                hoverBackground:
                                    "var(--element-button-custom-hover)",
                            }}
                            onClick={() =>
                                onSubmit({
                                    name: groupName,
                                    students: checkedStudents,
                                })
                            }
                            disabled={
                                checkedStudents.length === 0 ||
                                groupName === "" ||
                                !isOnline
                            }
                        />
                    </GridColumn>
                </Grid>
            </div>
        </>
    );
};

export default GroupForm;

const buildHierarchyPaths = (
    students: string[],
    classrooms: Group[] | undefined
) =>
    students.map((studentId) => {
        const classroom = classrooms?.find((classroom) =>
            classroom.students.some((student) => student.id === studentId)
        );
        if (!classroom) throw new Error("Student not found");
        return `${classroom.id}/${studentId}`;
    });

const defaultMessages = defineMessages({
    classroomList: {
        id: "group-form-classroomList",
        defaultMessage: "Student list",
    },
    noAddedStudents: {
        id: "group-form-noAddedStudents",
        defaultMessage: "No students yet",
    },
    cancel: {
        id: "group-form-cancel",
        defaultMessage: "Cancel",
    },
    groupCreationRemoveStudent: {
        id: "create-group-removeStudent",
        defaultMessage: "Remove student from the list",
    },
    padName: {
        id: "group-form-padName",
        defaultMessage: "Pad name",
    },
});
